import dayjs, { type Dayjs } from 'dayjs';
import type {
  TotalRevenueBreakdownItem,
  TotalRevenueBreakdownReference,
  TotalRevenueBreakdownTrendItem,
  TotalRevenueBreakdownTrendReference,
  TotalRevenueHotel,
  TotalRevenueItem,
  TotalRevenueReferenceItem,
  TotalRevenueTrendItem,
  TotalRevenueTrendReference,
} from 'lumen/models';

/**
 * Helper function to calculate the total capacity for a set of hotels for a given range.
 * @param from first day of the range
 * @param to last day of the range
 * @param hotels list of hotels to calculate the capacity for
 * @returns the total capacity for the given range and hotels
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function calculateTotalCapacity(
  from: Dayjs,
  to: Dayjs,
  ...hotels: TotalRevenueHotel[]
) {
  return hotels.reduce(
    (total, hotel) => hotel.calculateCapacityForRange(from, to) + total,
    0,
  );
}

/**
 * Helper function to calculate the date range of a trend item.
 * Takes the intersection of the item's date range and the current date range.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getDateRangeForTrendItem(
  item: TotalRevenueTrendItem | TotalRevenueBreakdownTrendItem,
  currentFrom: Dayjs,
  currentTo: Dayjs,
) {
  const fromDate = dayjs.max(item.date, currentFrom) ?? item.date;
  const endDate =
    dayjs.min(item.date.endOf(item.aggregation), currentTo) ??
    item.date.endOf(item.aggregation);

  return { fromDate, endDate };
}

function calculateAndAssignCapacityReferenceValues(
  referenceItem:
    | TotalRevenueReferenceItem
    | TotalRevenueBreakdownReference
    | TotalRevenueTrendReference
    | TotalRevenueBreakdownTrendReference,
  currentFrom: Dayjs,
  currentTo: Dayjs,
  ...hotels: TotalRevenueHotel[]
): void {
  let numberOfDays = currentTo.diff(currentFrom, 'day');

  if ('aggregation' in referenceItem.totalRevenueItem) {
    // TotalRevenueTrendReference | TotalRevenueBreakdownTrendReference
    const { fromDate, endDate } = getDateRangeForTrendItem(
      referenceItem.totalRevenueItem,
      currentFrom,
      currentTo,
    );

    numberOfDays = endDate.diff(fromDate, 'day');
  }

  const referenceToDate = referenceItem.referenceFromDate.add(
    numberOfDays,
    'day',
  );
  referenceItem.capacity = calculateTotalCapacity(
    referenceItem.referenceFromDate,
    referenceToDate,
    ...hotels,
  );
}

export function calculateAndAssignCapacity(
  item:
    | TotalRevenueItem
    | TotalRevenueBreakdownItem
    | TotalRevenueTrendItem
    | TotalRevenueBreakdownTrendItem,
  currentFrom: Dayjs,
  currentTo: Dayjs,
  ...hotels: TotalRevenueHotel[]
): void {
  let capacity = calculateTotalCapacity(currentFrom, currentTo, ...hotels);

  if ('aggregation' in item) {
    // TotalRevenueTrendItem | TotalRevenueBreakdownTrendItem
    const { fromDate, endDate } = getDateRangeForTrendItem(
      item,
      currentFrom,
      currentTo,
    );

    capacity = calculateTotalCapacity(fromDate, endDate, ...hotels);
  }

  item.capacity = capacity;

  if ('sources' in item) {
    // TotalRevenueBreakdownItem
    item.sources?.forEach((source) => {
      calculateAndAssignCapacity(source, currentFrom, currentTo, ...hotels);
    });

    [...item.trendDays, ...item.trendWeeks, ...item.trendMonths].forEach(
      (trendItem) => {
        calculateAndAssignCapacity(
          trendItem,
          currentFrom,
          currentTo,
          ...hotels,
        );
      },
    );
  }

  // Calculate the capacity for all reference values
  item.referenceValues.forEach((referenceValue) => {
    calculateAndAssignCapacityReferenceValues(
      referenceValue,
      currentFrom,
      currentTo,
      ...hotels,
    );
  });
}
